







































































import EventBus from '@/eventBus';
import CoButton from '@/components/Atoms/co-button/CoButton.vue';
import CoText from '@/components/Atoms/co-text/CoText.vue';
import CoAlert from '@/components/Molecules/co-alert/CoAlert.vue';
import CoModal from '@/components/Organisms/co-modal/CoModal.vue';

// Reserve the alert's one-line text, alert padding, and outer bottom padding before its exact height is measurable.
const RESPONSIVE_ALERT_FALLBACK_OFFSET = '2.56rem';

export default {
    name: 'CoAdminAlert',
    components: {
        CoAlert,
        CoButton,
        CoModal,
        CoText,
    },
    data() {
        return {
            lastTrackedLimitState: null,
            alertOffsetFrame: null,
            alertResizeObserver: null,
            observedAlertElement: null,
            isDestroying: false,
        };
    },
    computed: {
        limitReached() {
            if (this.$store?.state?.limits?.UserLimitReached) {
                return 'UserLimitReached';
            }
            if (this.$store?.state?.limits?.UserThresholdReached) {
                return 'UserThresholdReached';
            }
            return false;
        },
        isInMobile() {
            return this.$store?.state?.isInMobile;
        },
        isAdmin() {
            return this.$store?.state?.me?.Permissions?.includes('space_admin');
        },
        // The admin alert is shown when an admin has reached the user limit.
        isVisible() {
            return this.isAdmin && this.limitReached;
        },
        shouldApplyOffset() {
            if (!this.isVisible) {
                return false;
            }

            if (this.isInMobile) {
                // On mobile, the alert is positioned absolute within the main content area, so we don't need to apply an offset to the layout.
                return true;
            }

            return window.innerWidth <= 992;
        },
        limitTitle() {
            switch (this.limitReached) {
                case 'UserLimitReached':
                    return this.$t('labels.limitUsersReached');
                case 'UserThresholdReached':
                    return this.$t('labels.limitUsersAlmostReached');
                default:
                    return null;
            }
        },
        limitMessage() {
            switch (this.limitReached) {
                case 'UserLimitReached':
                    return this.$t('messages.limitUsersReached');
                case 'UserThresholdReached':
                    return this.$t('messages.limitUsersAlmostReached');
                default:
                    return null;
            }
        },
    },
    mounted() {
        window.addEventListener('resize', this.updateAlertOffset);
        if (typeof ResizeObserver !== 'undefined') {
            // Keep the shared layout offset aligned with the alert when responsive content changes its rendered height.
            this.alertResizeObserver = new ResizeObserver(this.updateAlertOffset);
        }
        this.updateAlertOffset();
        this.trackShownEvent();
    },
    beforeDestroy() {
        this.isDestroying = true;
        window.removeEventListener('resize', this.updateAlertOffset);
        this.alertResizeObserver?.disconnect();
        this.observedAlertElement = null;
        this.updateAlertOffset(true);
    },
    watch: {
        isVisible(newVal) {
            this.updateAlertOffset();
            if (!newVal) {
                this.lastTrackedLimitState = null;
                return;
            }

            this.trackShownEvent();
        },
        limitReached() {
            this.trackShownEvent();
        },
        isInMobile() {
            this.updateAlertOffset();
        },
        '$store.state.space.PlanSlug': function () {
            this.updateAlertOffset();
        },
    },
    methods: {
        trackShownEvent() {
            if (!this.isVisible || this.limitReached === this.lastTrackedLimitState) {
                return;
            }

            // Track each visible limit state once so async store updates still emit the expected Intercom events.
            EventBus.$emit('intercomTrackEvent', {
                eventName: 'paywall_shown',
            });

            if (this.limitReached === 'UserLimitReached') {
                EventBus.$emit('intercomTrackEvent', {
                    eventName: 'paywall_shown_user_limit_reached',
                });
            } else if (this.limitReached === 'UserThresholdReached') {
                EventBus.$emit('intercomTrackEvent', {
                    eventName: 'paywall_shown_user_limit_almost_reached',
                });
            }

            this.lastTrackedLimitState = this.limitReached;
        },
        getTopNavHeight() {
            if (!this.isInMobile) {
                return (
                    getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim() || '4.25rem'
                );
            }

            const mobileTopNav = document.querySelector('.co-top-nav-bar-mobile-wrapper') as HTMLElement | null;
            if (mobileTopNav && mobileTopNav.offsetHeight > 0) {
                return `${mobileTopNav.offsetHeight}px`;
            }

            return getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim() || '4.25rem';
        },
        // Keep the global header and alert offsets in sync so outer layouts can consume shared CSS variables.
        updateAlertOffset(reset = false) {
            if (reset === true) {
                if (this.alertOffsetFrame !== null) {
                    window.cancelAnimationFrame(this.alertOffsetFrame);
                    this.alertOffsetFrame = null;
                }

                // A responsive mode change can replace this instance; let the replacement retain ownership of the offset.
                window.requestAnimationFrame(() => {
                    if (!document.querySelector('.co-admin-alert')) {
                        document.documentElement.style.setProperty('--main-alert-offset', '0px');
                    }
                });
                return;
            }

            // Apply a safe offset immediately so the first responsive paint cannot overlap page content.
            document.documentElement.style.setProperty(
                '--main-alert-offset',
                this.shouldApplyOffset ? RESPONSIVE_ALERT_FALLBACK_OFFSET : '0px'
            );

            this.$nextTick(() => {
                if (this.isDestroying) {
                    return;
                }

                const alertElement = this.$el instanceof HTMLElement ? this.$el : null;
                if (this.alertResizeObserver && alertElement !== this.observedAlertElement) {
                    // Vue replaces the component root when v-if visibility changes, so observe only the current element.
                    this.alertResizeObserver.disconnect();
                    this.observedAlertElement = alertElement;
                    if (alertElement) {
                        this.alertResizeObserver.observe(alertElement);
                    }
                }

                if (this.alertOffsetFrame !== null) {
                    window.cancelAnimationFrame(this.alertOffsetFrame);
                }

                // Coalesce async store, resize, and ResizeObserver updates into the latest rendered measurement.
                this.alertOffsetFrame = window.requestAnimationFrame(() => {
                    this.alertOffsetFrame = null;
                    if (this.isDestroying) {
                        return;
                    }

                    document.documentElement.style.setProperty('--mobile-top-nav-height', this.getTopNavHeight());

                    const shouldResetOffset = !this.shouldApplyOffset;
                    const alertHeight = shouldResetOffset ? 0 : this.$el?.offsetHeight || 0;
                    document.documentElement.style.setProperty('--main-alert-offset', `${alertHeight}px`);
                });
            });
        },
        showModal() {
            if (this.$refs.adminAlertModal) {
                this.$refs.adminAlertModal.show();
            }
        },
        goToAdminAccount() {
            // record event in Intercom for feature usage tracking and targeted messaging
            EventBus.$emit('intercomTrackEvent', {
                eventName: `paywall_cta_click`,
                payload: {
                    feature: 'more_users',
                },
            });
            // also record a specific event for the triggered feature to allow granular analysis and targeting
            EventBus.$emit('intercomTrackEvent', {
                eventName: `paywall_cta_click_more_users`,
            });
            this.$router.push('/admin/account');
            // hide the modal
            this.$refs.adminAlertModal?.hide();
        },
    },
};
